import { Router, ScreenBase } from "@frui.ts/screens/dist";
import { interfaces } from "inversify";
import { action, computed, observable, reaction, toJS } from "mobx";

import PaymentResult from "models/paymentResult";
import LocalizationService from "services/localizationService";
import ChargeOrderService from "services/chargeOrderService";
import EventBus, {
  customerRatingOrderData,
  paymentConfirmationAvailable,
  paymentConfirmationError,
} from "../../../services/eventBus";
import GaService from "../../../services/gaService";
import PriceService from "../../../services/priceService";
import OrderSummaryViewModel from "./orderSummaryViewModel";
import { saveFileFromURL } from "../../../utils/fileUtils";
import NotificationService, { NotificationType } from "../../../services/notificationService";
import { IconType } from "@edaz/edazkit/Icon";
import ChargeOrder, { ChargeOrderStatus } from "models/chargeOrder";
import CustomerReviewViewModel from "viewModels/customerReview/customerReviewViewModel";
import TimeService from "services/timeService";

export const StatusViewNavigationName = "status";

@Router.registerRoute({ route: `${StatusViewNavigationName}/:orderId`, name: Router.Self })
export default class StatusViewModel extends ScreenBase {
  @observable showLoadingModal = false;
  result?: PaymentResult;
  orderSummaryViewModel: OrderSummaryViewModel;
  ratingViewModel: CustomerReviewViewModel;
  private unsubscriptions: (() => void)[] = [];

  @computed
  get isSuccess() {
    return this.chargeOrder._status === ChargeOrderStatus.Paid;
  }

  constructor(
    public chargeOrder: ChargeOrder,
    public localizationService: LocalizationService,
    public timeService: TimeService,
    private customerReviewViewModel: CustomerReviewViewModel,
    private orderSummaryViewModelFactory: ReturnType<typeof OrderSummaryViewModel.Factory>,
    public chargeOrderService: ChargeOrderService,
    private gaService: GaService,
    private eventBus: EventBus,
    private notificationService: NotificationService
  ) {
    super();
    this.navigationName = `${StatusViewNavigationName}/${this.chargeOrder.id}`;
    this.orderSummaryViewModel = this.orderSummaryViewModelFactory(this.chargeOrder);
    this.ratingViewModel = this.customerReviewViewModel;
    this.eventBus.subscribe(paymentConfirmationAvailable, this.onPaymentConfirmationIsAvailable);
    this.eventBus.subscribe(paymentConfirmationError, this.onPaymentConfirmationError);
    this.eventBus.publish(customerRatingOrderData({ orderId: this.chargeOrder.id }));
  }

  protected async onActivate(): Promise<Promise<any> | void> {
    await this.timeService.getCurrentServerTime();
  }
  navigate(subPath: string | undefined, params: any): Promise<any> | void | undefined {
    this.gaService.sendOrderInfo(this.chargeOrder);
    return super.navigate(subPath, params);
  }

  @action.bound
  private onPaymentConfirmationIsAvailable(data: ReturnType<typeof paymentConfirmationAvailable>) {
    this.showLoadingModal = false;
  }

  @action.bound
  private onPaymentConfirmationError(data: ReturnType<typeof paymentConfirmationError>) {
    this.showLoadingModal = false;
    this.notificationService.add({
      title: this.localizationService.translateError("generic"),
      type: NotificationType.ERROR,
      icon: IconType.Error,
    });
  }

  @computed get isMultiple() {
    return toJS(this.chargeOrder.charges).flatMap(item => item.licensePlates).length > 1;
  }

  @action.bound
  async getPaymentConfirmation() {
    if (this.showLoadingModal) {
      return;
    }

    this.showLoadingModal = true;

    try {
      await saveFileFromURL(this.chargeOrderService.getPaymentConfirmation, "Order status", { order: this.chargeOrder });
    } finally {
      this.showLoadingModal = false;
    }
  }

  get navigationParams() {
    const result = this.result;

    return { result };
  }

  static Factory({ container }: interfaces.Context) {
    return (chargeOrder: ChargeOrder) => {
      return new StatusViewModel(
        chargeOrder,
        container.get(LocalizationService),
        container.get(TimeService),
        container.get(CustomerReviewViewModel),
        container.get(OrderSummaryViewModel.Factory),
        container.get(ChargeOrderService),
        container.get(GaService),
        container.get(EventBus),
        container.get(NotificationService)
      );
    };
  }
}
